import React, { useEffect, useId, useRef, useState } from 'react';
import { FormActionsContext } from './FormActionsContext';
import { createPortal } from 'react-dom';
import { X } from '../assets/AppIcons';
import './entity-detail-dialog.css';
import IconActions from './IconActions';

const activeDetailDialogs = [];
let bodyModalSnapshot = null;

function snapshotElementState(element) {
  return {
    ariaHidden: element.getAttribute('aria-hidden'),
    externallyHidden: element.hasAttribute('data-aria-hidden'),
    inert: element.inert,
  };
}

function restoreElementState(element, snapshot) {
  // A Radix dialog can finish closing after this card opens. Do not restore its expired hiding state.
  if (snapshot.ariaHidden === null || (snapshot.externallyHidden && !element.hasAttribute('data-aria-hidden'))) {
    element.removeAttribute('aria-hidden');
  } else {
    element.setAttribute('aria-hidden', snapshot.ariaHidden);
  }
  element.inert = snapshot.inert;
}

function setElementInactive(element) {
  element.setAttribute('aria-hidden', 'true');
  element.inert = true;
}

function isFocusableCandidate(element) {
  return element instanceof HTMLElement && element.isConnected && !element.closest('[inert]');
}

function applyDetailDialogStack() {
  if (!bodyModalSnapshot || !activeDetailDialogs.length) return;

  bodyModalSnapshot.siblings.forEach(({ element }) => setElementInactive(element));
  const topDialog = activeDetailDialogs[activeDetailDialogs.length - 1];
  activeDetailDialogs.forEach((entry) => {
    if (entry === topDialog) {
      restoreElementState(entry.overlay, entry.overlayState);
    } else {
      setElementInactive(entry.overlay);
    }
  });
  document.body.style.overflow = 'hidden';
}

function registerDetailDialog(overlay, previouslyFocused) {
  if (!bodyModalSnapshot) {
    bodyModalSnapshot = {
      overflow: document.body.style.overflow,
      initiallyFocused: previouslyFocused,
      siblings: Array.from(document.body.children)
        .filter(
          (element) =>
            !element.classList.contains('entity-detail-dialog__overlay') &&
            !element.hasAttribute('data-wt-feedback-host')
        )
        .map((element) => ({
          element,
          ...snapshotElementState(element),
        })),
    };
  }

  const entry = {
    overlay,
    overlayState: snapshotElementState(overlay),
    previouslyFocused,
  };
  activeDetailDialogs.push(entry);
  applyDetailDialogStack();
  return entry;
}

function unregisterDetailDialog(entry) {
  const entryIndex = activeDetailDialogs.indexOf(entry);
  if (entryIndex === -1) return;

  const wasTopDialog = entryIndex === activeDetailDialogs.length - 1;
  activeDetailDialogs.splice(entryIndex, 1);
  restoreElementState(entry.overlay, entry.overlayState);

  if (activeDetailDialogs.length) {
    applyDetailDialogStack();
    if (wasTopDialog) {
      const nextTopDialog = activeDetailDialogs[activeDetailDialogs.length - 1];
      const focusTarget = isFocusableCandidate(entry.previouslyFocused)
        ? entry.previouslyFocused
        : nextTopDialog.overlay.querySelector('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      focusTarget?.focus?.();
    }
    return;
  }

  const snapshot = bodyModalSnapshot;
  bodyModalSnapshot = null;
  document.body.style.overflow = snapshot?.overflow || '';
  snapshot?.siblings.forEach(({ element, ...elementState }) => {
    restoreElementState(element, elementState);
  });

  const focusTarget = isFocusableCandidate(entry.previouslyFocused)
    ? entry.previouslyFocused
    : snapshot?.initiallyFocused;
  if (isFocusableCandidate(focusTarget)) {
    focusTarget.focus();
  }
}

function isTopDetailDialog(entry) {
  return activeDetailDialogs[activeDetailDialogs.length - 1] === entry;
}

function ActionGroup({ children, label, tone = 'default' }) {
  if (!children) return null;

  return (
    <div
      className={`entity-detail-dialog__footer-group entity-detail-dialog__footer-group--${tone}`}
      role="group"
      aria-label={label}
    >
      <IconActions>{children}</IconActions>
    </div>
  );
}

export default function EntityDetailDialog({
  isOpen,
  onClose,
  title,
  subtitle,
  avatarLabel,
  avatarUrl,
  badges = [],
  headerMeta = null,
  primaryActions = null,
  actions = null,
  editActions = null,
  dangerActions = null,
  children,
  footer = null,
  className = '',
}) {
  const titleId = useId();
  const [actionHost, setActionHost] = useState(null);
  const descriptionId = useId();
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const dialogEntry = registerDetailDialog(overlayRef.current, previouslyFocused);
    closeButtonRef.current?.focus();

    function handleKeyDown(event) {
      if (!isTopDetailDialog(dialogEntry)) return;

      if (event.target instanceof Node && dialogRef.current && !dialogRef.current.contains(event.target)) {
        return;
      }

      if (event.key === 'Escape') {
        if (event.defaultPrevented) return;
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusableElements.length) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      unregisterDetailDialog(dialogEntry);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <FormActionsContext.Provider value={actionHost}>
      <div
        ref={overlayRef}
        className="entity-detail-dialog__overlay"
        role="presentation"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose?.();
        }}
      >
        <section
          ref={dialogRef}
          className={`entity-detail-dialog ${className}`.trim()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={subtitle ? descriptionId : undefined}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className="entity-detail-dialog__header">
            <div className="entity-detail-dialog__identity">
              <div className="entity-detail-dialog__avatar" aria-hidden="true">
                {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{avatarLabel || '?'}</span>}
              </div>
              <div className="entity-detail-dialog__heading">
                <div className="entity-detail-dialog__title-row">
                <h2 id={titleId} className="select-text">
                  {title}
                </h2>
                {badges.length ? (
                  <div className="entity-detail-dialog__badges">
                    {badges.map((badge) => (
                      <span
                        key={`${badge.label}-${badge.tone || 'default'}`}
                        className={`entity-detail-dialog__badge ${badge.tone ? `is-${badge.tone}` : ''}`.trim()}
                      >
                        {badge.label}
                      </span>
                    ))}
                  </div>
                ) : null}
                {headerMeta ? <div className="entity-detail-dialog__header-meta">{headerMeta}</div> : null}
                </div>
                {subtitle ? (
                  <p id={descriptionId} className="select-text">{subtitle}</p>
                ) : null}
              </div>
            </div>
            <div className="entity-detail-dialog__header-controls">
              <button
                ref={closeButtonRef}
                type="button"
                className="entity-detail-dialog__close-icon"
                onClick={onClose}
                aria-label="Chiudi finestra"
              >
                <X size={32} aria-hidden="true" />
                <span className="sr-only">Chiudi finestra</span>
              </button>
            </div>
          </header>

          <div className="entity-detail-dialog__body">{children}</div>

          <footer className="entity-detail-dialog__footer">
            <div className="form-footer-tools" ref={setActionHost} />
            {footer || primaryActions ? (
              <div className="entity-detail-dialog__footer-primary">
                <ActionGroup label="Azioni dettaglio">{footer}</ActionGroup>
                <ActionGroup label="Azioni di avanzamento" tone="primary">
                  {primaryActions}
                </ActionGroup>
              </div>
            ) : null}
            {actions || editActions || dangerActions ? (
              <div className="entity-detail-dialog__footer-actions">
                <ActionGroup label="Azioni ordinarie">{actions}</ActionGroup>
                <ActionGroup label="Modifica" tone="edit">
                  {editActions}
                </ActionGroup>
                <ActionGroup label="Azioni distruttive" tone="danger">
                  {dangerActions}
                </ActionGroup>
              </div>
            ) : null}
          </footer>
        </section>
      </div>
    </FormActionsContext.Provider>,
    document.body
  );
}
